import React, { useState, useEffect, useCallback } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserIcon,
  TruckIcon,
  IdentificationIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import { User,Badge } from 'lucide-react';
import apiService from '../../services/api';

interface Convoyeur {
  id: number;
  nom: string;
  prenom: string;
  nom_complet?: string;
  nom_complet_display?: string;
  numero_identification?: string;
  type_identification?: string;
  type_identification_display?: string;
  telephone?: string;
  telephone_secondaire?: string;
  email?: string;
  adresse?: string;
  ville?: string;
  entreprise?: string;
  fonction?: string;
  numero_badge?: string;
  statut?: string;
  statut_display?: string;
  est_autorise: boolean;
  camion_attache?: number | null;
  camion_attache_info?: {
    id: number;
    immatriculation: string;
    marque?: string;
    modele?: string;
  } | null;
  niveau_acces?: string;
  niveau_acces_display?: string;
  zones_autorisees?: string;
  certifications?: string;
  formations?: string;
  date_derniere_formation?: string | null;
  nombre_accompagnements?: number;
  date_dernier_accompagnement?: string | null;
  notes?: string;
  observations_securite?: string;
  created_at?: string;
  updated_at?: string;
}

interface ConvoyeurFormData {
  nom: string;
  prenom: string;
  numero_identification?: string;
  type_identification?: string;
  telephone?: string;
  telephone_secondaire?: string;
  email?: string;
  adresse?: string;
  ville?: string;
  entreprise?: string;
  fonction?: string;
  numero_badge?: string;
  statut?: string;
  est_autorise: boolean;
  camion_attache?: number | null;
  niveau_acces?: string;
  zones_autorisees?: string;
  certifications?: string;
  formations?: string;
  notes?: string;
  observations_securite?: string;
}

const ConvoyeurManagement: React.FC = () => {
  const [convoyeurs, setConvoyeurs] = useState<Convoyeur[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingConvoyeur, setEditingConvoyeur] = useState<Convoyeur | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterNiveau, setFilterNiveau] = useState('');
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;
  const [showAdvancedFields, setShowAdvancedFields] = useState(false);

  const [formData, setFormData] = useState<ConvoyeurFormData>({
    nom: '',
    prenom: '',
    numero_identification: '',
    type_identification: 'CNI',
    telephone: '',
    telephone_secondaire: '',
    email: '',
    adresse: '',
    ville: '',
    entreprise: '',
    fonction: '',
    numero_badge: '',
    statut: 'ACTIF',
    est_autorise: true,
    camion_attache: null,
    niveau_acces: 'STANDARD',
    zones_autorisees: '',
    certifications: '',
    formations: '',
    notes: '',
    observations_securite: ''
  });

  const identificationTypes = [
    { value: 'CNI', label: 'CNI' },
    { value: 'PASSEPORT', label: 'Passeport' },
    { value: 'PERMIS', label: 'Permis' },
    { value: 'BADGE', label: 'Badge' },
    { value: 'AUTRE', label: 'Autre' }
  ];

  const statusOptions = [
    { value: 'ACTIF', label: 'Actif' },
    { value: 'INACTIF', label: 'Inactif' },
    { value: 'SUSPENDU', label: 'Suspendu' },
    { value: 'BLOQUE', label: 'Bloqué' }
  ];

  const niveauAccesOptions = [
    { value: 'BASIC', label: 'Basique' },
    { value: 'STANDARD', label: 'Standard' },
    { value: 'ELEVE', label: 'Élevé' },
    { value: 'ADMIN', label: 'Admin' }
  ];

  const fetchConvoyeurs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        page_size: pageSize,
        ordering: 'nom,prenom', // Tri alphabétique par nom puis prénom
        search: searchTerm || undefined,
        statut: filterStatus || undefined,
        type_identification: filterType || undefined,
        niveau_acces: filterNiveau || undefined
      };
      
      // Ajouter un timestamp pour éviter le cache
      const paramsWithCacheBust = {
        ...params,
        _t: Date.now()
      };
      const response = await apiService.get('/weighing/api/convoyeurs/', { params: paramsWithCacheBust });
      
      if (response && response.data) {
        setConvoyeurs(response.data.results || []);
        setTotalCount(response.data.count || 0);
        setTotalPages(Math.ceil((response.data.count || 0) / pageSize));
      }
    } catch (err) {
      console.error('ConvoyeurManagement - Erreur fetchConvoyeurs:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, filterStatus, filterType, filterNiveau]);

  useEffect(() => {
    fetchConvoyeurs();
  }, [fetchConvoyeurs]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingConvoyeur) {
        await apiService.put(`/weighing/api/convoyeurs/${editingConvoyeur.id}/`, formData);
      } else {
        await apiService.post('/weighing/api/convoyeurs/', formData);
      }

      setShowModal(false);
      setEditingConvoyeur(null);
      resetForm();
      await fetchConvoyeurs();
      
      // Afficher un message de succès
      setError(null);
    } catch (err: any) {
      console.error('ConvoyeurManagement - Erreur:', err);
      
      // Gestion d'erreur plus détaillée
      let errorMessage = 'Erreur inconnue';
      
      if (err.response) {
        // Erreur HTTP avec réponse
        if (err.response.status === 401) {
          errorMessage = 'Session expirée. Veuillez vous reconnecter.';
        } else if (err.response.status === 400) {
          errorMessage = 'Données invalides. Vérifiez les champs requis.';
        } else if (err.response.status === 403) {
          errorMessage = 'Permissions insuffisantes.';
        } else if (err.response.status === 500) {
          errorMessage = 'Erreur serveur. Contactez l\'administrateur.';
        } else {
          errorMessage = `Erreur ${err.response.status}: ${err.response.data?.detail || err.response.data?.message || 'Erreur inconnue'}`;
        }
      } else if (err.request) {
        errorMessage = 'Impossible de contacter le serveur.';
      } else {
        errorMessage = err.message || 'Erreur inconnue';
      }
      
      setError(errorMessage);
    }
  };

  const handleEdit = (convoyeur: Convoyeur) => {
    setEditingConvoyeur(convoyeur);
    setFormData({
      nom: convoyeur.nom ?? '',
      prenom: convoyeur.prenom ?? '',
      numero_identification: convoyeur.numero_identification ?? '',
      type_identification: convoyeur.type_identification ?? 'CNI',
      telephone: convoyeur.telephone ?? '',
      telephone_secondaire: convoyeur.telephone_secondaire ?? '',
      email: convoyeur.email ?? '',
      adresse: convoyeur.adresse ?? '',
      ville: convoyeur.ville ?? '',
      entreprise: convoyeur.entreprise ?? '',
      fonction: convoyeur.fonction ?? '',
      numero_badge: convoyeur.numero_badge ?? '',
      statut: convoyeur.statut ?? 'ACTIF',
      est_autorise: convoyeur.est_autorise ?? true,
      camion_attache: convoyeur.camion_attache,
      niveau_acces: convoyeur.niveau_acces ?? 'STANDARD',
      zones_autorisees: convoyeur.zones_autorisees ?? '',
      certifications: convoyeur.certifications ?? '',
      formations: convoyeur.formations ?? '',
      notes: convoyeur.notes ?? '',
      observations_securite: convoyeur.observations_securite ?? ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce convoyeur ?')) return;

    try {
      await apiService.delete(`/weighing/api/convoyeurs/${id}/`);
      fetchConvoyeurs();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    }
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      prenom: '',
      numero_identification: '',
      type_identification: 'CNI',
      telephone: '',
      telephone_secondaire: '',
      email: '',
      adresse: '',
      ville: '',
      entreprise: '',
      fonction: '',
      numero_badge: '',
      statut: 'ACTIF',
      est_autorise: true,
      camion_attache: null,
      niveau_acces: 'STANDARD',
      zones_autorisees: '',
      certifications: '',
      formations: '',
      notes: '',
      observations_securite: ''
    });
    setShowAdvancedFields(false);
  };

  // Fonctions de pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset à la première page lors de la recherche
  };

  const handleFilterChange = (filterType: string, value: string) => {
    switch (filterType) {
      case 'status':
        setFilterStatus(value);
        break;
      case 'type':
        setFilterType(value);
        break;
      case 'niveau':
        setFilterNiveau(value);
        break;
    }
    setCurrentPage(1); // Reset à la première page lors du filtrage
  };

  const getConvoyeurIcon = (type?: string) => {
    if (!type) return <User className="h-5 w-5" />;
    switch (type) {
      case 'CNI': return <UserIcon className="h-5 w-5" />;
      case 'PASSEPORT': return <IdentificationIcon className="h-5 w-5" />;
      case 'PERMIS': return <TruckIcon className="h-5 w-5" />;
      case 'BADGE': return <Badge className="h-5 w-5" />;
      default: return <User className="h-5 w-5" />;
    }
  };

  // Le filtrage est maintenant géré côté serveur - pas de filteredConvoyeurs

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }


  return (
    <div className="space-y-6">
      {/* Header avec actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Gestion des Convoyeurs</h3>
          <p className="text-sm text-gray-600">Gérez les convoyeurs et accompagnateurs</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditingConvoyeur(null);
            setShowModal(true);
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Nouveau Convoyeur
        </button>
      </div>

      {/* Filtres et recherche */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="">Tous les statuts</option>
            {statusOptions.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
          <select
            value={filterType}
            onChange={(e) => handleFilterChange('type', e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="">Tous les types ID</option>
            {identificationTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
          <select
            value={filterNiveau}
            onChange={(e) => handleFilterChange('niveau', e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="">Tous les niveaux</option>
            {niveauAccesOptions.map(niveau => (
              <option key={niveau.value} value={niveau.value}>{niveau.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Liste des convoyeurs */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Convoyeur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Identification
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Entreprise
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Camion
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {convoyeurs && convoyeurs.length > 0 ? convoyeurs.map((convoyeur) => (
                <tr key={convoyeur.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                          {getConvoyeurIcon(convoyeur.type_identification)}
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{convoyeur.nom_complet_display}</div>
                        <div className="text-sm text-gray-500">{convoyeur.fonction}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{convoyeur.numero_identification}</div>
                    <div className="text-sm text-gray-500">{convoyeur.type_identification_display}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{convoyeur.entreprise}</div>
                    <div className="text-sm text-gray-500">{convoyeur.telephone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {convoyeur.camion_attache_info ? (
                      <div>
                        <div className="text-sm text-gray-900">{convoyeur.camion_attache_info.immatriculation}</div>
                        <div className="text-sm text-gray-500">{convoyeur.camion_attache_info.marque} {convoyeur.camion_attache_info.modele}</div>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">Aucun camion</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {convoyeur.est_autorise ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-1" />
                      ) : (
                        <XCircleIcon className="h-5 w-5 text-red-500 mr-1" />
                      )}
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        convoyeur.statut === 'ACTIF' ? 'bg-green-100 text-green-800' :
                        convoyeur.statut === 'INACTIF' ? 'bg-red-100 text-red-800' :
                        convoyeur.statut === 'SUSPENDU' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {convoyeur.statut_display}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(convoyeur)}
                        className="text-orange-600 hover:text-orange-900"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(convoyeur.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <UserIcon className="h-12 w-12 text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun convoyeur</h3>
                      <p className="text-sm text-gray-500">
                        {searchTerm || filterStatus || filterType || filterNiveau 
                          ? 'Aucun convoyeur ne correspond à vos critères de recherche.' 
                          : 'Commencez par ajouter votre premier convoyeur.'
                        }
                      </p>
                      {!searchTerm && !filterStatus && !filterType && !filterNiveau && (
                        <button
                          onClick={() => {
                            resetForm();
                            setEditingConvoyeur(null);
                            setShowModal(true);
                          }}
                          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                        >
                          <PlusIcon className="h-5 w-5 mr-2" />
                          Ajouter un convoyeur
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Contrôles de pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Affichage de <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> à <span className="font-medium">
                    {Math.min(currentPage * pageSize, totalCount)}
                  </span> sur <span className="font-medium">{totalCount}</span> résultats
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  
                  {/* Numéros de page */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pageNum === currentPage
                            ? 'z-10 bg-orange-50 border-orange-500 text-orange-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de création/édition */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[10000]">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingConvoyeur ? 'Modifier le Convoyeur' : 'Nouveau Convoyeur'}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nom *</label>
                    <input
                      type="text"
                      required
                      value={formData.nom}
                      onChange={(e) => setFormData({...formData, nom: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Prénom *</label>
                    <input
                      type="text"
                      required
                      value={formData.prenom}
                      onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">N° Identification </label>
                    <input
                      type="text"
                      value={formData.numero_identification}
                      onChange={(e) => setFormData({...formData, numero_identification: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Type ID</label>
                    <select
                      value={formData.type_identification}
                      onChange={(e) => setFormData({...formData, type_identification: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      {identificationTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Téléphone</label>
                    <input
                      type="tel"
                      value={formData.telephone}
                      onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Téléphone Secondaire</label>
                    <input
                      type="tel"
                      value={formData.telephone_secondaire}
                      onChange={(e) => setFormData({...formData, telephone_secondaire: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">N° Badge</label>
                    <input
                      type="text"
                      value={formData.numero_badge}
                      onChange={(e) => setFormData({...formData, numero_badge: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Entreprise</label>
                    <input
                      type="text"
                      value={formData.entreprise}
                      onChange={(e) => setFormData({...formData, entreprise: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Fonction</label>
                    <input
                      type="text"
                      value={formData.fonction}
                      onChange={(e) => setFormData({...formData, fonction: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Ville</label>
                    <input
                      type="text"
                      value={formData.ville}
                      onChange={(e) => setFormData({...formData, ville: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Niveau d'accès</label>
                    <select
                      value={formData.niveau_acces}
                      onChange={(e) => setFormData({...formData, niveau_acces: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      {niveauAccesOptions.map(niveau => (
                        <option key={niveau.value} value={niveau.value}>{niveau.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Adresse</label>
                  <textarea
                    rows={2}
                    value={formData.adresse}
                    onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                {/* Bouton pour afficher/masquer les champs avancés */}
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => setShowAdvancedFields(!showAdvancedFields)}
                    className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-orange-600 hover:text-orange-700 border border-orange-300 rounded-md hover:bg-orange-50 transition-colors"
                  >
                    <span>Plus d'informations</span>
                    {showAdvancedFields ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>

                {/* Champs avancés - masqués par défaut */}
                {showAdvancedFields && (
                  <div className="space-y-4 border-t border-gray-200 pt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Zones Autorisées</label>
                      <textarea
                        rows={2}
                        value={formData.zones_autorisees}
                        onChange={(e) => setFormData({...formData, zones_autorisees: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Certifications</label>
                        <textarea
                          rows={3}
                          value={formData.certifications}
                          onChange={(e) => setFormData({...formData, certifications: e.target.value})}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Formations</label>
                        <textarea
                          rows={3}
                          value={formData.formations}
                          onChange={(e) => setFormData({...formData, formations: e.target.value})}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Notes</label>
                      <textarea
                        rows={3}
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Observations Sécurité</label>
                      <textarea
                        rows={2}
                        value={formData.observations_securite}
                        onChange={(e) => setFormData({...formData, observations_securite: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Statut *</label>
                    <select
                      required
                      value={formData.statut}
                      onChange={(e) => setFormData({...formData, statut: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      {statusOptions.map(status => (
                        <option key={status.value} value={status.value}>{status.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="est_autorise"
                        checked={formData.est_autorise}
                        onChange={(e) => setFormData({...formData, est_autorise: e.target.checked})}
                        className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      />
                      <label htmlFor="est_autorise" className="ml-2 block text-sm text-gray-900">
                        Autorisé
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                  >
                    {editingConvoyeur ? 'Modifier' : 'Créer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <XCircleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Erreur</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConvoyeurManagement;
